import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { authService } from '@/lib/auth-service';

interface TemporalAuraAuthenticatorProps {
  onAuthenticated: (result: any) => void;
}

export const TemporalAuraAuthenticator: React.FC<TemporalAuraAuthenticatorProps> = ({
  onAuthenticated
}) => {
  const [scanning, setScanning] = useState(false);
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [auraData, setAuraData] = useState<any>(null);
  const [progress, setProgress] = useState(0);

  const performAuraScan = async () => {
    if (!name.trim() || !dateOfBirth) {
      alert('Please enter your name and date of birth for aura scanning');
      return;
    }

    setScanning(true);
    setProgress(0);

    // Simulate scanning progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);

    try {
      const result = await authService.performAuraScan({
        name: name.trim(),
        dateOfBirth,
        biometricData: generateBiometricData(),
        temporalSignature: generateTemporalSignature()
      });

      if (result.success) {
        setAuraData(result);
        onAuthenticated(result);
      } else {
        console.error('Aura scan failed:', result.error);
      }
    } catch (error) {
      console.error('Aura scan error:', error);
    } finally {
      setScanning(false);
      clearInterval(progressInterval);
    }
  };
  const generateBiometricData = () => {
    return {
      heartRate: Math.floor(Math.random() * 40) + 60,
      brainwaves: Math.random(),
      auricField: Math.random() * 100
    };
  };

  const generateTemporalSignature = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/30">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-purple-300 flex items-center justify-center gap-2">
            👁️ Temporal Aura Authentication
          </CardTitle>
          <p className="text-purple-200/80">
            Enter your name and birth date for precise aura scanning
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {!scanning && !auraData && (
            <div className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-purple-300">Guardian Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    className="bg-purple-900/20 border-purple-500/30 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="dateOfBirth" className="text-purple-300">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="bg-purple-900/20 border-purple-500/30 text-white"
                  />
                </div>
              </div>
              
              <div className="text-center space-y-4">
                <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center animate-pulse">
                  💜
                </div>
                <Button 
                  onClick={performAuraScan}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                  size="lg"
                  disabled={!name.trim() || !dateOfBirth}
                >
                  ⚡ Begin Aura Scan
                </Button>
              </div>
            </div>
          )}

          {scanning && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center animate-spin">
                  👁️
                </div>
                <p className="text-purple-300 mt-4">Scanning {name}'s temporal aura signature...</p>
              </div>
              <div className="w-full bg-purple-900/30 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center text-purple-200/60">
                {progress < 30 && "Detecting heart purity..."}
                {progress >= 30 && progress < 60 && "Analyzing temporal alignment..."}
                {progress >= 60 && progress < 90 && "Verifying intentions..."}
                {progress >= 90 && "Completing authentication..."}
              </p>
            </div>
          )}

          {auraData && (
            <div className="space-y-4">
              <div className="text-center">
                {auraData.accessGranted ? (
                  <div className="space-y-2">
                    <div className="text-6xl">🛡️</div>
                    <Badge className="bg-green-600 text-white">
                      PURE HEART VERIFIED
                    </Badge>
                    <p className="text-green-300">{auraData.message}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-6xl">⚠️</div>
                    <Badge className="bg-red-600 text-white">
                      AURA IMPURITIES DETECTED
                    </Badge>
                    <p className="text-red-300">{auraData.message}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-purple-300 text-sm">Heart Purity</p>
                  <div className="w-full bg-purple-900/30 rounded-full h-2 mt-1">
                    <div 
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${auraData.auraReading.purity}%` }}
                    />
                  </div>
                  <p className="text-purple-200/60 text-xs">{auraData.auraReading.purity.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Temporal Alignment</p>
                  <div className="w-full bg-purple-900/30 rounded-full h-2 mt-1">
                    <div 
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${auraData.auraReading.temporalAlignment}%` }}
                    />
                  </div>
                  <p className="text-purple-200/60 text-xs">{auraData.auraReading.temporalAlignment.toFixed(1)}%</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-purple-300 text-sm">Spiritual Signature</p>
                <p className="text-purple-200/80 font-mono text-xs bg-purple-900/20 p-2 rounded">
                  {auraData.auraReading.spiritualSignature}
                </p>
              </div>

              {!auraData.accessGranted && (
                <Button 
                  onClick={performAuraScan}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Retry Aura Scan
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};